import { useMutation, useQuery } from 'react-query';
import { httpRequest } from '../config/api';

// Exported like `periodService`: the top bar reads a period's file and sheet mappings outside React
// Query's hooks, to answer "which sheet is this one, over there?" before it navigates.
export const automationService = {
  getFundMappings: (periodId, params) =>
    httpRequest.get(`api/automation/${periodId}/automation-fund-mappings`, {
      ...params
    }),
  getFileMappings: (periodId, params) =>
    httpRequest.get(`api/automation/${periodId}/automation-file-mappings`, {
      params
    }),

  saveMappings: ({ periodId, data }) =>
    httpRequest.post(`api/automation/${periodId}/save-mappings`, data),

  deleteFileMappings: fileMappingId =>
    httpRequest.delete(`api/automation/delete-file-mapping/${fileMappingId}`),

  deleteSheetMappings: sheetMappingId =>
    httpRequest.delete(`api/automation/delete-sheet-mapping/${sheetMappingId}`),

  createFileMappings: ({ periodId, data }) =>
    httpRequest.post(`api/automation/${periodId}/create-file-mappings`, data),

  createSheetMappings: data =>
    httpRequest.post(`api/automation/${data.fileMappingId}/create-sheet-mappings`, data.data),

  submitStatus: ({ periodId, data }) =>
    httpRequest.post(`api/period/${periodId}/submit-status`, data),

  copyPreviousAIFs: data => httpRequest.post('api/automation/copy-input-funds', data),
  copyPreviousApprovers: data => httpRequest.post('api/copy-approvers', data),
  copyJurisdictions: data => httpRequest.post('api/automation/copy-jurisdictions', data),
  automateExtraction: data => httpRequest.post('api/automation/automate-extraction', data),

  automateReportGeneration: data => httpRequest.post('api/automation/automate-report', data),
  automateTransformation: data => httpRequest.post('api/automation/automate-transformation', data),
  automateSelectedTransformation: data =>
    httpRequest.post('api/automation/automate-selected-transformations', data),
  automateReExtraction: data => httpRequest.post('api/automation/automate-re-extraction', data),
  cancelAutomateExtraction: data =>
    httpRequest.put('api/automation/cancel-automate-extraction-process/', data),
  cancelAutomateTransformation: data =>
    httpRequest.put('api/automation/cancel-automate-transformation-process/', data)
};

export const useDeleteFileMapping = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.deleteFileMappings, queryParams);
};

export const useDeleteSheetMapping = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.deleteSheetMappings, queryParams);
};

export const useAutomateReExtraction = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.automateReExtraction, queryParams);
};

export const useCancelAutomateExtraction = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.cancelAutomateExtraction, queryParams);
};

export const useCancelAutomateTransformation = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.cancelAutomateTransformation, queryParams);
};

export const useAutomateSelectedTransformation = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.automateSelectedTransformation, queryParams);
};

export const useCreateFileMappings = ({ periodId, queryParams = {} } = {}) => {
  return useMutation(data => automationService.createFileMappings({ periodId, data }), queryParams);
};

export const useSaveAutomationMappings = ({ queryParams = {} } = {}) => {
  return useMutation(
    ({ periodId, data }) => automationService.saveMappings({ periodId, data }),
    queryParams
  );
};

export const useCreateSheetMappings = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.createSheetMappings(data), queryParams);
};

export const useCopyAIFs = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.copyPreviousAIFs(data), queryParams);
};

export const useCopyJurisdictions = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.copyJurisdictions(data), queryParams);
};

export const useCopyApprovers = ({ queryParams = {} } = {}) => {
  return useMutation(automationService.copyPreviousApprovers, queryParams);
};

export const useAutomateExtraction = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.automateExtraction(data), queryParams);
};

export const useAutomateReportGeneration = ({ queryParams = {} } = {}) => {
  return useMutation(
    (/** @type {any} */ data) => automationService.automateReportGeneration(data),
    queryParams
  );
};

export const useAutomateTransformation = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.automateTransformation(data), queryParams);
};

export const useSubmitStatus = ({ queryParams = {} } = {}) => {
  return useMutation(data => automationService.submitStatus(data), queryParams);
};

export const useGetAutomationFundMappings = ({ params = {}, periodId, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_AUTOMATION_FUND_MAPPINGS', periodId, params],
    () => automationService.getFundMappings(periodId, params),
    queryParams
  );
};

export const useGetAutomationFileMappings = ({ params = {}, periodId, queryParams = {} } = {}) => {
  return useQuery(
    ['GET_AUTOMATION_FILE_MAPPINGS', periodId, params],
    () => automationService.getFileMappings(periodId, params),
    queryParams
  );
};
